import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar as CalendarGlyph,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Plus,
  UserRoundCheck,
  UserRoundPlus,
  XCircle,
} from 'lucide-react'
import { api } from '@/api/client'
import { KIND_COLORS, type CalendarEvent } from '@/api/calendar'
import { useToast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { t, type DashboardLocale } from '@/pages/agent/dashboard/copy'
import {
  KIND_ICONS,
  SeriesGlyph,
  dayKey,
  groupEventsByDay,
  monthCellsFor,
  startOfDay,
} from '@/pages/agent/calendar/shared'
import {
  AddAppointmentDialog,
  CancelEventDialog,
  RescheduleForm,
  type AddPrefill,
} from '@/pages/agent/calendar/AppointmentDialogs'
import { MemberActionDialog, type MemberActionMode } from '@/pages/agent/calendar/MemberActionDialog'

export interface CalendarWidgetProps {
  events: CalendarEvent[]
  /** True when the calendar API failed (module off / network) and no data loaded. */
  error: boolean
  loading: boolean
  locale: DashboardLocale
  onEventCreated: (event: CalendarEvent) => void
  onEventUpdated: (event: CalendarEvent) => void
  /** Wave 6b — removes a cancelled occurrence from the parent's list. */
  onEventRemoved?: (id: string) => void
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return String(err)
}

export function CalendarWidget({
  events,
  error,
  loading,
  locale,
  onEventCreated,
  onEventUpdated,
  onEventRemoved,
}: CalendarWidgetProps) {
  const { addToast } = useToast()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'upcoming' | 'month'>('upcoming')
  const [addOpen, setAddOpen] = useState(false)
  const [prefill, setPrefill] = useState<AddPrefill>(null)
  const [rescheduleTarget, setRescheduleTarget] = useState<CalendarEvent | null>(null)
  const [memberAction, setMemberAction] = useState<{ event: CalendarEvent; mode: MemberActionMode; targetId?: string } | null>(null)
  const [cancelTarget, setCancelTarget] = useState<CalendarEvent | null>(null)
  const [patchBusy, setPatchBusy] = useState(false)

  const openAdd = (next: AddPrefill = null) => {
    setPrefill(next)
    setAddOpen(true)
  }

  /**
   * Optimistic PATCH: apply locally, reconcile with the server response (or
   * revert). Wave 6b: a series target PATCHes the series id (whole-series
   * v1); the response is the parent row, which the parent state (keyed by
   * occurrence ids) does not carry — the optimistic row stays authoritative
   * until the next fetch.
   */
  const patchEvent = async (target: CalendarEvent, patch: Record<string, unknown>, okKey: Parameters<typeof t>[0]) => {
    if (patchBusy) return
    setPatchBusy(true)
    const seriesId = target.series_id ?? null
    const optimistic: CalendarEvent = { ...target, ...(patch as Partial<CalendarEvent>) }
    onEventUpdated(optimistic)
    try {
      const { event } = await api.updateCalendarEvent(seriesId ?? target.id, patch)
      if (!seriesId) onEventUpdated(event)
      addToast({ title: t(okKey, locale), variant: 'success' })
      setRescheduleTarget(null)
      setCancelTarget(null)
    } catch (err) {
      onEventUpdated(target)
      addToast({
        title: t('calendar.toast.updateError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    } finally {
      setPatchBusy(false)
    }
  }

  /** Wave 6b — cancel ONE occurrence: POST /:series_id/cancel-occurrence. */
  const cancelOccurrence = async (target: CalendarEvent) => {
    if (patchBusy) return
    setPatchBusy(true)
    try {
      await api.cancelCalendarOccurrence(target.series_id ?? target.id, target.occurrence_start ?? target.starts_at)
      // No optimistic removal before success: the parent can only drop the
      // row once the server accepted the cancellation.
      onEventRemoved?.(target.id)
      addToast({ title: t('calendar.toast.cancelled', locale), variant: 'success' })
      setCancelTarget(null)
    } catch (err) {
      addToast({
        title: t('calendar.toast.cancelError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    } finally {
      setPatchBusy(false)
    }
  }

  /** Cancel dialog confirm: occurrence-only for instances, PATCH otherwise. */
  const confirmCancel = (scope: 'occurrence' | 'series') => {
    const target = cancelTarget
    if (!target) return
    if (scope === 'occurrence' && (target.is_recurrence_instance || target.series_id)) {
      void cancelOccurrence(target)
    } else {
      void patchEvent(target, { status: 'cancelled' }, 'calendar.toast.cancelled')
    }
  }

  /** §3.4 Reassign / Delegate: POSTs target the series id for instances. */
  const openMemberAction = (event: CalendarEvent, mode: MemberActionMode) =>
    setMemberAction({ event, mode, targetId: event.series_id ?? undefined })

  /** §3.4 Send message: open the conversation with the linked contact, prefilled with the event context. */
  const sendMessageTo = async (event: CalendarEvent) => {
    if (!event.contact_id) return
    const context = `${event.title} · ${new Intl.DateTimeFormat(locale, {
      day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit',
    }).format(new Date(event.starts_at))}`
    try {
      const conversations = await api.getConversations()
      const match = (conversations || []).find((c) => c.contact_id === event.contact_id)
      if (match) {
        navigate(`/inbox?conversation=${encodeURIComponent(match.id)}&draft=${encodeURIComponent(context)}`)
      } else {
        navigate('/inbox')
        addToast({ title: t('calendar.sendMessage.noConversation', locale), variant: 'default' })
      }
    } catch {
      navigate('/inbox')
    }
  }

  const upcomingGroups = useMemo(() => {
    const start = startOfDay(new Date())
    const limit = new Date(start)
    limit.setDate(limit.getDate() + 30)
    const inWindow = events.filter((e) => {
      const d = new Date(e.starts_at)
      return !Number.isNaN(d.getTime()) && d >= start && d < limit
    })
    return groupEventsByDay(inWindow)
  }, [events])

  const monthEvents = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const event of events) {
      const d = new Date(event.starts_at)
      if (Number.isNaN(d.getTime())) continue
      const key = dayKey(d)
      const bucket = map.get(key)
      if (bucket) bucket.push(event)
      else map.set(key, [event])
    }
    return map
  }, [events])

  const [monthCursor, setMonthCursor] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
  })

  const monthCells = useMemo(
    () => monthCellsFor(monthCursor.year, monthCursor.month), // Monday-start grid.
    [monthCursor],
  )

  const weekdayLabels = useMemo(() => {
    // Monday-start (product convention); 2024-01-01 was a Monday.
    const base = new Date(2024, 0, 1)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base)
      d.setDate(base.getDate() + i)
      return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(d)
    })
  }, [locale])

  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
        new Date(monthCursor.year, monthCursor.month, 1),
      ),
    [locale, monthCursor],
  )

  const todayKey = dayKey(new Date())

  const shiftMonth = (delta: number) => {
    setMonthCursor((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1)
      return { year: d.getFullYear(), month: d.getMonth() }
    })
  }

  const timeLabel = (event: CalendarEvent): string => {
    if (event.all_day) return t('calendar.allDay', locale)
    const d = new Date(event.starts_at)
    if (Number.isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(d)
  }

  const dateLabelForKey = (key: string): string => {
    if (key === todayKey) return t('calendar.today', locale)
    const tomorrow = startOfDay(new Date())
    tomorrow.setDate(tomorrow.getDate() + 1)
    if (key === dayKey(tomorrow)) return t('calendar.tomorrow', locale)
    const d = new Date(`${key}T00:00:00`)
    return new Intl.DateTimeFormat(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(d)
  }

  const renderSkeleton = () => (
    <div className="space-y-[var(--lc-space-sm)]" data-testid="calendar-skeleton">
      {Array.from({ length: 6 }, (_, i) => (
        <div key={i} className="flex items-center gap-[var(--lc-space-sm)]">
          <div className="h-3 w-12 animate-pulse rounded-[var(--lc-radius-sm)] bg-[var(--lc-surface-sunken)]" />
          <div
            className="h-4 flex-1 animate-pulse rounded-[var(--lc-radius-sm)] bg-[var(--lc-surface-sunken)]"
            style={{ animationDelay: `${i * 90}ms` }}
          />
        </div>
      ))}
    </div>
  )

  const renderUpcoming = () => {
    if (upcomingGroups.length === 0) {
      return (
        <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
          {error ? t('calendar.error', locale) : t('calendar.empty', locale)}
        </p>
      )
    }
    return (
      <div className="space-y-[var(--lc-space-md)]">
        {upcomingGroups.map(([key, group]) => (
          <section key={key}>
            <h4
              className="mb-1 text-[var(--lc-text-muted)]"
              style={{ font: 'var(--lc-type-overline)', letterSpacing: 'var(--lc-tracking-overline)' }}
            >
              {dateLabelForKey(key)}
            </h4>
            <ul>
              {group.map((event) => {
                const KindIcon = KIND_ICONS[event.kind] ?? CalendarGlyph
                const colour = KIND_COLORS[event.kind]
                const cancelled = event.status === 'cancelled'
                return (
                  <li
                    key={event.id}
                    className="flex items-center gap-[var(--lc-space-sm)] border-b border-[var(--lc-border)] py-[var(--lc-space-2xs)]"
                  >
                    <span
                      className="w-14 shrink-0 tabular-nums text-[var(--lc-text-muted)]"
                      style={{ font: 'var(--lc-type-caption)' }}
                    >
                      {timeLabel(event)}
                    </span>
                    <span
                      className="h-2 w-2 shrink-0 rounded-pill"
                      style={{ backgroundColor: colour }}
                      aria-hidden="true"
                    />
                    <KindIcon className="h-4 w-4 shrink-0" style={{ color: colour }} aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      {event.is_recurrence_instance ? (
                        <span className="flex items-center gap-[var(--lc-space-2xs)]">
                          <span
                            className={cn('block truncate', cancelled && 'text-[var(--lc-text-muted)] line-through')}
                            style={{ font: 'var(--lc-type-body-sm)' }}
                          >
                            {event.title}
                          </span>
                          <SeriesGlyph locale={locale} className="text-[var(--lc-text-muted)]" />
                        </span>
                      ) : (
                        <span
                          className={cn('block truncate', cancelled && 'text-[var(--lc-text-muted)] line-through')}
                          style={{ font: 'var(--lc-type-body-sm)' }}
                        >
                          {event.title}
                        </span>
                      )}
                      {event.description ? (
                        <span
                          className={cn('block truncate text-[var(--lc-text-muted)]', cancelled && 'line-through')}
                          style={{ font: 'var(--lc-type-caption)' }}
                        >
                          {event.description}
                        </span>
                      ) : null}
                    </span>
                    {event.status === 'pending' ? (
                      <Badge variant="underOffer">{t('calendar.status.pending', locale)}</Badge>
                    ) : null}
                    {cancelled ? <Badge variant="unpublished">{t('calendar.status.cancelled', locale)}</Badge> : null}
                    <span className="flex shrink-0 items-center">
                      <QuickAction
                        label={t('calendar.action.reschedule', locale)}
                        onClick={() => setRescheduleTarget(event)}
                      >
                        <CalendarClock className="h-4 w-4" aria-hidden="true" />
                      </QuickAction>
                      <QuickAction label={t('calendar.action.cancel', locale)} onClick={() => setCancelTarget(event)}>
                        <XCircle className="h-4 w-4" aria-hidden="true" />
                      </QuickAction>
                      <QuickAction
                        label={event.contact_id ? t('calendar.action.message', locale) : t('calendar.noLinkedContact', locale)}
                        disabled={!event.contact_id}
                        onClick={() => void sendMessageTo(event)}
                      >
                        <MessageSquare className="h-4 w-4" aria-hidden="true" />
                      </QuickAction>
                      <QuickAction
                        label={t('calendar.action.reassign', locale)}
                        onClick={() => openMemberAction(event, 'reassign')}
                      >
                        <UserRoundPlus className="h-4 w-4" aria-hidden="true" />
                      </QuickAction>
                      <QuickAction
                        label={t('calendar.action.delegate', locale)}
                        onClick={() => openMemberAction(event, 'delegate')}
                      >
                        <UserRoundCheck className="h-4 w-4" aria-hidden="true" />
                      </QuickAction>
                    </span>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </div>
    )
  }

  const renderMonth = () => (
    <div>
      <div className="mb-[var(--lc-space-sm)] flex items-center justify-between">
        <span className="text-[var(--lc-text-heading)]" style={{ font: 'var(--lc-type-body-sm)' }}>
          {monthLabel}
        </span>
        <span className="flex items-center">
          <QuickAction label={t('calendar.prevMonth', locale)} onClick={() => shiftMonth(-1)}>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </QuickAction>
          <QuickAction label={t('calendar.nextMonth', locale)} onClick={() => shiftMonth(1)}>
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </QuickAction>
        </span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {weekdayLabels.map((label) => (
          <span
            key={label}
            className="text-center text-[var(--lc-text-muted)]"
            style={{ font: 'var(--lc-type-caption)' }}
          >
            {label}
          </span>
        ))}
        {monthCells.map((cell, i) => {
          if (cell.day === null) return <span key={`blank-${i}`} />
          const key = dayKey(new Date(monthCursor.year, monthCursor.month, cell.day))
          const dayEvents = monthEvents.get(key) ?? []
          const extra = dayEvents.length - 3
          return (
            <button
              key={key}
              type="button"
              onClick={() => {
                if (cell.day === null) return
                // Wave 2: the full diary owns day-level detail — open it at
                // the clicked day (header + day-click both link per relay).
                navigate(`/agent/calendar?day=${key}`)
              }}
              className={cn(
                'flex min-h-tap flex-col items-center gap-0.5 rounded-[var(--lc-radius-sm)] p-1 hover:bg-[var(--lc-surface-sunken)]',
                key === todayKey && 'bg-[var(--lc-surface-selected)] ring-1 ring-[var(--lc-action-primary)]',
              )}
              aria-label={`${key}${dayEvents.length ? ` · ${dayEvents.length}` : ''}`}
            >
              <span className="tabular-nums" style={{ font: 'var(--lc-type-caption)' }}>
                {cell.day}
              </span>
              <span className="flex h-3 items-center gap-0.5">
                {dayEvents.slice(0, 3).map((event) => (
                  <span
                    key={event.id}
                    className="h-1.5 w-1.5 rounded-pill"
                    style={{ backgroundColor: KIND_COLORS[event.kind] }}
                    aria-hidden="true"
                  />
                ))}
                {extra > 0 ? (
                  <span className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                    +{extra}
                  </span>
                ) : null}
              </span>
            </button>
          )
        })}
      </div>
      <button
        type="button"
        onClick={() => openAdd()}
        className="mt-[var(--lc-space-sm)] inline-flex min-h-tap w-full items-center justify-center gap-1 rounded-[var(--lc-radius-md)] border border-dashed border-[var(--lc-border)] text-[var(--lc-text-muted)] hover:border-[var(--lc-action-primary)] hover:text-[var(--lc-text-brand)]"
        style={{ font: 'var(--lc-type-caption)' }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t('calendar.add', locale)}
      </button>
    </div>
  )

  return (
    <div className="flex h-full min-h-0 flex-col gap-[var(--lc-space-sm)]">
      <div className="flex shrink-0 items-center justify-between gap-[var(--lc-space-sm)]">
        <div
          role="tablist"
          aria-label={t('widget.title.calendar', locale)}
          className="inline-flex rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-0.5"
        >
          {(
            [
              ['upcoming', 'calendar.mode.upcoming'],
              ['month', 'calendar.mode.month'],
            ] as const
          ).map(([value, key]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              onClick={() => setMode(value)}
              className={cn(
                'min-h-tap rounded-[var(--lc-radius-sm)] px-3 transition-colors',
                mode === value
                  ? 'bg-[var(--lc-surface-raised)] text-[var(--lc-text-primary)] shadow-[var(--lc-elevation-sm)]'
                  : 'text-[var(--lc-text-muted)] hover:text-[var(--lc-text-primary)]',
              )}
              style={{ font: 'var(--lc-type-caption)' }}
            >
              {t(key, locale)}
            </button>
          ))}
        </div>
        <span className="flex items-center">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('calendar.openDiary', locale)}
            title={t('calendar.openDiary', locale)}
            onClick={() => navigate('/agent/calendar')}
          >
            <CalendarGlyph className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('calendar.add', locale)}
            title={t('calendar.add', locale)}
            onClick={() => openAdd()}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
          </Button>
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading && events.length === 0 ? renderSkeleton() : mode === 'upcoming' ? renderUpcoming() : renderMonth()}
      </div>

      <AddAppointmentDialog
        key={`${addOpen ? 'open' : 'closed'}-${prefill?.when ?? ''}-${prefill?.kind ?? ''}`}
        open={addOpen}
        onOpenChange={setAddOpen}
        prefill={prefill}
        locale={locale}
        onCreated={(event) => {
          onEventCreated(event)
          setAddOpen(false)
        }}
      />

      <Dialog open={rescheduleTarget !== null} onOpenChange={(open) => !open && setRescheduleTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('calendar.reschedule.title', locale)}</DialogTitle>
          </DialogHeader>
          {rescheduleTarget ? (
            <RescheduleForm
              event={rescheduleTarget}
              locale={locale}
              busy={patchBusy}
              onCancel={() => setRescheduleTarget(null)}
              onSubmit={(patch) => patchEvent(rescheduleTarget, patch, 'calendar.toast.updated')}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <MemberActionDialog
        event={memberAction?.event ?? null}
        mode={memberAction?.mode ?? 'reassign'}
        targetId={memberAction?.targetId}
        locale={locale}
        onClose={() => setMemberAction(null)}
        onApplied={(updated) => {
          // Whole-series v1: the response is the parent row — patch the
          // fields the occurrence row shows instead of swapping ids.
          if (memberAction?.targetId) {
            onEventUpdated({
              ...memberAction.event,
              owner_user_id: updated.owner_user_id ?? memberAction.event.owner_user_id,
              assigned_proxy_user_id:
                updated.assigned_proxy_user_id ?? memberAction.event.assigned_proxy_user_id,
            })
          } else {
            onEventUpdated(updated)
          }
          setMemberAction(null)
        }}
      />

      <CancelEventDialog
        event={cancelTarget}
        locale={locale}
        busy={patchBusy}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
      />
    </div>
  )
}

function QuickAction({
  label,
  title,
  disabled,
  onClick,
  children,
}: {
  label: string
  title?: string
  disabled?: boolean
  onClick?: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title ?? label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex h-tap min-h-tap w-tap min-w-tap items-center justify-center rounded-[var(--lc-radius-md)] text-[var(--lc-text-muted)] transition-colors hover:bg-[var(--lc-surface-sunken)] hover:text-[var(--lc-text-primary)] disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  )
}
